import { useEffect, useRef } from 'react';
import { notification, Button } from 'antd';
import { createElement } from 'react';

/**
 * 偵測後台有沒有部署新版本。
 *
 * 後台是 SPA：分頁如果在部署前就開著、之後沒重新整理，就會一直跑舊的 JS——
 * 客戶因此兩次回報「還是沒修」，但其實線上早就修好了。這裡定期比對
 * index.html 裡的 bundle 檔名（Vite 會加 hash），不一樣就提示重新整理。
 *
 * **不自動重整**：編輯中的文章可能有還沒存的內容（見 useAutosave），
 * 一律由使用者自己按按鈕。
 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** 目前這個分頁載入的 bundle 檔名。 */
function currentBundle(): string {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return el?.src.match(BUNDLE_RE)?.[0] ?? '';
}

/** 線上最新的 bundle 檔名（不吃快取）。 */
async function latestBundle(): Promise<string> {
  const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) return '';
  return (await res.text()).match(BUNDLE_RE)?.[0] ?? '';
}

export function useNewVersion(): void {
  const notified = useRef(false);

  useEffect(() => {
    const mine = currentBundle();
    if (!mine) return; // 開發模式沒有 hash 檔名，不用檢查

    const check = async (): Promise<void> => {
      if (notified.current || document.hidden) return;
      try {
        const latest = await latestBundle();
        if (!latest || latest === mine) return;
        notified.current = true;
        notification.info({
          key: 'new-version',
          message: '後台已更新',
          description: '請重新整理以載入最新版本。編輯中的內容會先自動儲存，不會遺失。',
          duration: 0,
          placement: 'bottomRight',
          btn: createElement(
            Button,
            { type: 'primary', size: 'small', onClick: () => window.location.reload() },
            '重新整理',
          ),
        });
      } catch {
        /* 網路暫時有問題就下次再檢查 */
      }
    };

    void check();
    const timer = setInterval(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
}
